import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import Modal from '../../components/common/Modal';
import Pagination from '../../components/common/Pagination';
import StatusBadge from '../../components/common/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import { getReservationList, createReservation, updateReservationStatus } from '../../api/reservation';
import { getStoreList, getStoreTables } from '../../api/store';
import { formatDateTime } from '../../utils/format';

const STATUS_OPTIONS = [
  { value: '', label: '전체 상태' },
  { value: 'REQUESTED', label: '예약대기' },
  { value: 'CONFIRMED', label: '예약확정' },
  { value: 'REJECTED', label: '거절됨' },
  { value: 'CANCELLED', label: '취소' },
  { value: 'COMPLETED', label: '이용완료' },
  { value: 'NO_SHOW', label: '노쇼' },
];

function formatReservationDateTime(date, time) {
  if (!date) return '-';
  const hhmm = (time || '').slice(0, 5);
  return `${date} ${hhmm}`;
}

function reserverLabel(r) {
  if (r.memberId) return `${r.memberName || '(회원)'} · 회원`;
  return `${r.guestName || '(비회원)'} · 비회원`;
}

function reserverPhone(r) {
  return r.memberId ? r.memberPhone : r.guestPhone;
}

export default function ReservationManagePage() {
  const { auth } = useAuth();
  const [stores, setStores] = useState([]);
  const [condition, setCondition] = useState({ pageNo: 1, pageSize: 15, storeId: '', reservationStatus: '', reservationDate: '' });
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusTarget, setStatusTarget] = useState(null); // 처리(상태변경) 대상 예약
  const [createModalOpen, setCreateModalOpen] = useState(false);

  useEffect(() => {
    getStoreList().then((res) => setStores(res.data || [])).catch(() => setStores([]));
  }, []);

  const fetchList = useCallback(async (cond, silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const res = await getReservationList(cond);
      setPage(res.data);
    } catch (err) {
      if (!silent) setError(err.response?.data?.message || '예약 목록을 불러오지 못했습니다.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(condition); }, [condition, fetchList]);

  // 예약대기 건이 실시간으로 새로 들어올 수 있으므로 조용히 폴링한다.
  useEffect(() => {
    const interval = setInterval(() => fetchList(condition, true), 15000);
    return () => clearInterval(interval);
  }, [condition, fetchList]);

  return (
    <AdminLayout title="예약관리" menuUrl="/admin/reservation">
      <div className="page-header">
        <div>
          <h1>예약관리</h1>
          <div className="subtitle">회원/비회원의 테이블 예약 신청을 확인하고 승인·거절·테이블 배정을 처리합니다.</div>
        </div>
      </div>

      <div className="list-toolbar">
        <div className="search-bar">
          <select className="select" value={condition.storeId} onChange={(e) => setCondition((c) => ({ ...c, storeId: e.target.value, pageNo: 1 }))}>
            <option value="">전체 매장</option>
            {stores.map((s) => <option key={s.storeId} value={s.storeId}>{s.storeName}</option>)}
          </select>
          <select className="select" value={condition.reservationStatus} onChange={(e) => setCondition((c) => ({ ...c, reservationStatus: e.target.value, pageNo: 1 }))}>
            {STATUS_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
          <input
            className="input"
            type="date"
            value={condition.reservationDate}
            onChange={(e) => setCondition((c) => ({ ...c, reservationDate: e.target.value, pageNo: 1 }))}
            style={{ maxWidth: 160 }}
          />
          {condition.reservationDate && (
            <button className="btn btn-ghost btn-sm" onClick={() => setCondition((c) => ({ ...c, reservationDate: '', pageNo: 1 }))}>날짜 초기화</button>
          )}
          <button className="btn btn-secondary" onClick={() => fetchList(condition)}>새로고침</button>
        </div>
        <div className="list-toolbar-right">
          <button className="btn btn-primary" onClick={() => setCreateModalOpen(true)}>+ 예약 등록</button>
        </div>
      </div>

      <div className="list-count-bar">
        <span className="list-count">전체 <b>{page?.totalCount ?? 0}</b>건</span>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>예약번호</th><th>매장</th><th>예약일시</th><th>인원</th><th>예약자</th><th>연락처</th><th>배정테이블</th><th>상태</th><th>관리</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="empty-row">불러오는 중...</td></tr>}
              {!loading && error && <tr><td colSpan={9} className="empty-row error-state">{error}</td></tr>}
              {!loading && !error && page?.list?.length === 0 && <tr><td colSpan={9} className="empty-row">예약 내역이 없습니다.</td></tr>}
              {!loading && !error && page?.list?.map((r) => (
                <tr key={r.reservationId}>
                  <td>{r.reservationId}</td>
                  <td>{r.storeName}</td>
                  <td>{formatReservationDateTime(r.reservationDate, r.reservationTime)}</td>
                  <td>{r.partySize}명</td>
                  <td>{reserverLabel(r)}</td>
                  <td>{reserverPhone(r) || '-'}</td>
                  <td>{r.tableNo || <span className="text-tertiary">미배정</span>}</td>
                  <td><StatusBadge value={r.reservationStatus} label={STATUS_OPTIONS.find((s) => s.value === r.reservationStatus)?.label} /></td>
                  <td className="actions">
                    <button className="btn btn-secondary btn-sm" onClick={() => setStatusTarget(r)}>처리</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {page && <Pagination {...page} onChange={(pageNo) => setCondition((c) => ({ ...c, pageNo }))} />}

      {statusTarget && (
        <ReservationStatusModal
          reservation={statusTarget}
          adminName={auth.adminName}
          onClose={() => setStatusTarget(null)}
          onSaved={() => { setStatusTarget(null); fetchList(condition); }}
        />
      )}

      {createModalOpen && (
        <ReservationFormModal
          stores={stores}
          adminName={auth.adminName}
          onClose={() => setCreateModalOpen(false)}
          onSaved={() => { setCreateModalOpen(false); fetchList(condition); }}
        />
      )}
    </AdminLayout>
  );
}

/** 예약 승인/거절/취소/완료/노쇼 처리 + 테이블 배정 모달 */
function ReservationStatusModal({ reservation, adminName, onClose, onSaved }) {
  const [reservationStatus, setReservationStatus] = useState(reservation.reservationStatus);
  const [tableId, setTableId] = useState(reservation.tableId || '');
  const [adminMemo, setAdminMemo] = useState(reservation.adminMemo || '');
  const [tables, setTables] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getStoreTables(reservation.storeId).then((res) => setTables(res.data || [])).catch(() => setTables([]));
  }, [reservation.storeId]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await updateReservationStatus(reservation.reservationId, {
        reservationStatus,
        tableId: tableId ? Number(tableId) : null,
        adminMemo,
        updatedBy: adminName,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || '처리에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`예약 #${reservation.reservationId} 처리`}
      onClose={onClose}
      width={420}
      footer={<>
        <button className="btn btn-secondary" onClick={onClose}>취소</button>
        <button className="btn btn-primary" onClick={submit} disabled={saving}>{saving ? '저장 중...' : '저장'}</button>
      </>}
    >
      <form onSubmit={submit}>
        <div className="text-secondary" style={{ fontSize: 12.5, marginBottom: 12 }}>
          {reservation.storeName} · {reservation.reservationDate} {(reservation.reservationTime || '').slice(0, 5)} · {reservation.partySize}명 · {reserverLabel(reservation)}
          {reserverPhone(reservation) && <> ({reserverPhone(reservation)})</>}
        </div>

        {error && <div className="text-danger" style={{ marginBottom: 10, fontSize: 12.5 }}>{error}</div>}

        <div className="field">
          <label>예약 상태</label>
          <select className="select" value={reservationStatus} onChange={(e) => setReservationStatus(e.target.value)}>
            {STATUS_OPTIONS.filter((s) => s.value).map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>

        <div className="field">
          <label>배정 테이블 (선택)</label>
          <select className="select" value={tableId} onChange={(e) => setTableId(e.target.value)}>
            <option value="">미배정</option>
            {tables.map((t) => <option key={t.tableId} value={t.tableId}>{t.tableNo}</option>)}
          </select>
        </div>

        <div className="field">
          <label>관리자 메모 (거절 사유 등)</label>
          <textarea className="input" rows={3} value={adminMemo} onChange={(e) => setAdminMemo(e.target.value)} />
        </div>

        {reservation.requestMemo && (
          <div className="text-secondary" style={{ fontSize: 12.5 }}>예약자 요청사항: {reservation.requestMemo}</div>
        )}
      </form>
    </Modal>
  );
}

/** 관리자가 전화 등으로 접수한 예약을 직접 등록하는 모달 (기본 확정상태) */
function ReservationFormModal({ stores, adminName, onClose, onSaved }) {
  const [form, setForm] = useState({
    storeId: '',
    tableId: '',
    reservationDate: '',
    reservationTime: '',
    partySize: 2,
    isMember: false,
    memberId: '',
    guestName: '',
    guestPhone: '',
    requestMemo: '',
  });
  const [tables, setTables] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!form.storeId) { setTables([]); return; }
    getStoreTables(form.storeId).then((res) => setTables(res.data || [])).catch(() => setTables([]));
  }, [form.storeId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.storeId || !form.reservationDate || !form.reservationTime) {
      setError('매장/예약일자/예약시간은 필수입니다.');
      return;
    }
    if (form.isMember && !form.memberId) {
      setError('회원ID를 입력하세요.');
      return;
    }
    if (!form.isMember && (!form.guestName.trim() || !form.guestPhone.trim())) {
      setError('비회원 예약은 예약자명과 연락처가 필수입니다.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await createReservation({
        storeId: Number(form.storeId),
        tableId: form.tableId ? Number(form.tableId) : null,
        memberId: form.isMember ? Number(form.memberId) : null,
        guestName: form.isMember ? null : form.guestName,
        guestPhone: form.isMember ? null : form.guestPhone,
        reservationDate: form.reservationDate,
        reservationTime: form.reservationTime,
        partySize: Number(form.partySize),
        requestMemo: form.requestMemo,
        createdBy: adminName,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || '등록에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="예약 등록 (관리자 직접 접수)"
      onClose={onClose}
      width={460}
      footer={<>
        <button className="btn btn-secondary" onClick={onClose}>취소</button>
        <button className="btn btn-primary" onClick={submit} disabled={saving}>{saving ? '저장 중...' : '등록'}</button>
      </>}
    >
      <form onSubmit={submit}>
        {error && <div className="text-danger" style={{ marginBottom: 10, fontSize: 12.5 }}>{error}</div>}

        <div className="field">
          <label>매장</label>
          <select className="select" value={form.storeId} onChange={(e) => setForm({ ...form, storeId: e.target.value, tableId: '' })} required>
            <option value="">선택하세요</option>
            {stores.map((s) => <option key={s.storeId} value={s.storeId}>{s.storeName}</option>)}
          </select>
        </div>

        <div className="field">
          <label>배정 테이블 (선택)</label>
          <select className="select" value={form.tableId} onChange={(e) => setForm({ ...form, tableId: e.target.value })} disabled={!form.storeId}>
            <option value="">미배정</option>
            {tables.map((t) => <option key={t.tableId} value={t.tableId}>{t.tableNo}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <div className="field" style={{ flex: 1 }}>
            <label>예약일자</label>
            <input className="input" type="date" value={form.reservationDate} onChange={(e) => setForm({ ...form, reservationDate: e.target.value })} required />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label>예약시간</label>
            <input className="input" type="time" value={form.reservationTime} onChange={(e) => setForm({ ...form, reservationTime: e.target.value })} required />
          </div>
          <div className="field" style={{ width: 90 }}>
            <label>인원수</label>
            <input className="input" type="number" min="1" value={form.partySize} onChange={(e) => setForm({ ...form, partySize: e.target.value })} required />
          </div>
        </div>

        <div className="field checkbox-row">
          <input type="checkbox" id="isMember" checked={form.isMember} onChange={(e) => setForm({ ...form, isMember: e.target.checked })} />
          <label htmlFor="isMember" style={{ margin: 0 }}>회원 예약 (회원ID로 직접 연결)</label>
        </div>

        {form.isMember ? (
          <div className="field">
            <label>회원ID</label>
            <input className="input" type="number" value={form.memberId} onChange={(e) => setForm({ ...form, memberId: e.target.value })} placeholder="회원관리 화면에서 회원ID 확인" />
          </div>
        ) : (
          <>
            <div className="field">
              <label>예약자명</label>
              <input className="input" value={form.guestName} onChange={(e) => setForm({ ...form, guestName: e.target.value })} />
            </div>
            <div className="field">
              <label>연락처</label>
              <input className="input" value={form.guestPhone} onChange={(e) => setForm({ ...form, guestPhone: e.target.value })} placeholder="010-0000-0000" />
            </div>
          </>
        )}

        <div className="field">
          <label>요청사항 (선택)</label>
          <textarea className="input" rows={2} value={form.requestMemo} onChange={(e) => setForm({ ...form, requestMemo: e.target.value })} />
        </div>
      </form>
    </Modal>
  );
}
